import * as React from "react"
import { useTracker } from "@modules/analytics"
import { useToggleSlicesAPI } from "../hooks/useToggleSlicesAPI"
import {
  siteBuildsSettings as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { Link, Spacer, ToggleCheckbox, Badge } from "gatsby-interface"
import { FormattedMessage } from "@modules/locales"
import { MdInfoOutline, MdArrowForward } from "react-icons/md"
import { useSiteDependencyInfo } from "../hooks/useSiteDependencyInfo"
import { useWorkspaceDetailsShortcuts } from "@modules/organization/builds/hooks/useWorkspaceDetailsShortcuts"
import { useFlags } from "@modules/featureFlags"
import { getPathToUpgradeWorkspacePlan } from "@modules/organization/shared/utils"

export type EnableSlicesAPIProps = {
  siteId: string
  organizationId: string
  enabled: boolean
  fastHostingEnabled: boolean
}

export function EnableSlicesAPI({
  siteId,
  enabled,
  organizationId,
  fastHostingEnabled,
}: EnableSlicesAPIProps) {
  const workspace = useWorkspaceDetailsShortcuts(organizationId)
  const { flags } = useFlags()

  const [toggleSlicesAPIEnabled, { errorAlert }] = useToggleSlicesAPI(siteId)
  const { trackButtonClicked } = useTracker()
  const { isCompatibleGatsbyVersion } = useSiteDependencyInfo(siteId, `>=5.0.0`)

  const readMoreLink = `https://gatsby.dev/slices-optimization`

  // we only allow slices on enterprise plans, or any if we aren't enforcing it in launchdarkly
  const isAllowedByPlan =
    workspace.isOnEnterprisePlan || !flags.enforceSlicesApiEnterprise

  // if we aren't allowed by plan or the min gatsby version isn't met, show a message
  let reasonLockedMessage = null
  if (!isAllowedByPlan) {
    const linkToPlanUpgrade = getPathToUpgradeWorkspacePlan(organizationId)

    reasonLockedMessage = (
      <FormattedMessage
        message={text.messages.onlyCustomizableOnEnterprisePlan}
        tags={{
          link: function (content: string) {
            return (
              <Link to={linkToPlanUpgrade} variant="SIMPLE">
                {content} <MdArrowForward />
              </Link>
            )
          },
        }}
      />
    )
  } else if (!fastHostingEnabled) {
    reasonLockedMessage = (
      <FormattedMessage message={text.messages.enableSlicesAPIGatsbyJSIO} />
    )
  } else if (!isCompatibleGatsbyVersion) {
    // in this case, it's technically not "locked", but the message is still needed
    reasonLockedMessage = (
      <FormattedMessage
        message={text.messages.enableSlicesAPILatestGatsbyVersion}
        tags={{
          link: function (content: string) {
            return (
              <Link to={readMoreLink} variant="SIMPLE">
                {content} <MdArrowForward />
              </Link>
            )
          },
        }}
      />
    )
  }

  // We aren't guarding on the Gatsby 5 requirement
  // Users will likely try this in a branch first, so let's not
  // require it, but still show the reasonLockedMessage message
  const isDisabled = !isAllowedByPlan || !fastHostingEnabled

  return (
    <React.Fragment>
      <ToggleCheckbox
        id="enable-slices-api-checkbox"
        label={
          <React.Fragment>
            {text.labels.enableSlicesAPI}
            <Spacer direction="horizontal" size={3} />
            <Link href={readMoreLink}>
              <MdInfoOutline />
            </Link>
            <Spacer direction="horizontal" size={3} />
            <Badge tone="SUCCESS" size="S" textVariant="DEFAULT">
              {uiText.labels.beta}
            </Badge>
          </React.Fragment>
        }
        hint={
          <React.Fragment>
            {text.messages.enableSlicesAPI}
            {". "}
            {reasonLockedMessage}
          </React.Fragment>
        }
        checked={fastHostingEnabled ? !!enabled : false}
        onChange={() => {
          trackButtonClicked(`update slices api`, {
            uiSource: `Site Settings`,
          })
          toggleSlicesAPIEnabled()
        }}
        disabled={isDisabled}
      />
      {errorAlert}
    </React.Fragment>
  )
}
